import React,{ useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  Paper,
} from '@mui/material';
import {
  TrendingUp,
  Inventory,
  Warning,
  Assignment,
  Business,
  Notifications,
  PendingActions,
} from '@mui/icons-material';
import { analyticsAPI } from '../../services/API';
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
  <Card 
    sx={{ 
      height: '100%', 
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            color="text.secondary" 
            gutterBottom 
            variant="h6"
            fontWeight="medium"
          >
            {title}
          </Typography>
          <Typography 
            variant="h2" 
            component="div" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: `${color}.main`, ml: 2 }}>
          {React.cloneElement(icon, { sx: { fontSize: 48 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const InvDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const analyticsResponse = await analyticsAPI.getDashboard();
      setDashboardData(analyticsResponse.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h4" color="error">
          Error loading dashboard data
        </Typography>
      </Box>
    );
  }

  const { overview, alerts } = dashboardData;

  return (

    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h2" 
          gutterBottom 
          fontWeight="bold"
          sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}
        >
          Welcome back, {user?.name}!
        </Typography>
        <Typography 
          variant="h5" 
          color="text.secondary" 
          sx={{ mb: 2 }}
        >
          Here's what's happening in your inventory today.
        </Typography>
      </Box>

      {/* Primary Statistics Grid */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Items"
            value={dashboardData.counts?.items || 0}
            subtitle="Active items"
            icon={<Inventory />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Active Departments"
            value={dashboardData.counts?.departments || 0}
            subtitle="Departments"
            icon={<Business />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Pending Approvals"
            value={overview.pendingApprovals || 0}
            subtitle="Require action"
            icon={<PendingActions />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Low Stock Alerts"
            value={overview.lowStockCount}
            subtitle="Items need attention"
            icon={<Warning />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Secondary Statistics Grid */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Today's Issues"
            value={overview.todayTransactions}
            subtitle={`Cost: ₹${overview.todayCost?.toLocaleString() || 0}`}
            icon={<Assignment />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Monthly Issues"
            value={dashboardData.counts?.totalIssues || 0}
            subtitle={`Cost: ₹${overview.monthlyCost?.toLocaleString() || 0}`}
            icon={<TrendingUp />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Expiring Soon"
            value={overview.expiryAlertCount}
            subtitle="Check inventory"
            icon={<Notifications />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Pending Purchases"
            value={overview.pendingPurchases || 0}
            subtitle="Orders in progress"
            icon={<Assignment />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Content Section */}
      <Grid container spacing={4}>
        {/* Recent Item Issues */}
        <Grid item xs={12} lg={8}>
          <Paper 
            elevation={2}
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="h4" 
                gutterBottom 
                fontWeight="bold"
                sx={{ mb: 3 }}
              >
                Recent Item Issues
              </Typography>
              <List sx={{ pt: 0 }}>
                {dashboardData.recentIssues?.map((issue, index) => (
                  <ListItem 
                    key={issue._id} 
                    divider={index < dashboardData.recentIssues.length - 1}
                    sx={{ 
                      py: 2,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Assignment color="primary" sx={{ fontSize: 28 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="h6" fontWeight="medium">
                          Issue: {issue.issueNumber}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1" color="text.secondary">
                          {issue.department?.departmentName || 'N/A'} • 
                          ₹{issue.grandTotal?.toLocaleString() || 0} • 
                          {new Date(issue.issueDate).toLocaleDateString()}
                        </Typography>
                      }
                      sx={{ 
                        '& .MuiListItemText-primary': { mb: 0.5 },
                        '& .MuiListItemText-secondary': { fontSize: '1rem' }
                      }}
                    />
                    <Chip 
                      label={issue.status} 
                      size="medium"
                      sx={{ 
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        minWidth: 120
                      }}
                      color={
                        issue.status === 'issued' ? 'success' : 
                        issue.status === 'approved' ? 'info' : 
                        issue.status === 'pending_approval' ? 'warning' : 
                        'default'
                      }
                    />
                  </ListItem>
                ))}
                {(!dashboardData.recentIssues || dashboardData.recentIssues.length === 0) && (
                  <ListItem sx={{ py: 3 }}>
                    <ListItemText 
                      primary={
                        <Typography variant="h6" color="text.secondary">
                          No recent item issues
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">
                          Item issues will appear here
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Paper>
        </Grid>

        {/* Alerts Section */}
        <Grid item xs={12} lg={4}>
          <Paper 
            elevation={2}
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="h4" 
                gutterBottom 
                fontWeight="bold"
                sx={{ mb: 3 }}
              >
                Important Alerts
              </Typography>
              
              {/* Low Stock Alerts */}
              <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
                Low Stock Items
              </Typography>
              <List dense sx={{ mb: 3 }}>
                {alerts.lowStock?.slice(0, 3).map((batch, index) => (
                  <ListItem 
                    key={index} 
                    divider={index < 2}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Warning color="warning" sx={{ fontSize: 26 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="h6" fontWeight="medium">
                          {batch.item?.name || 'Unknown Item'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1" color="text.secondary">
                          Only {batch.availableQuantity} left
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                {(!alerts.lowStock || alerts.lowStock.length === 0) && (
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="h6" color="text.secondary">
                          No low stock alerts
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">
                          All items are well stocked
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>

              {/* Expiry Alerts */}
              <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                Expiring Soon
              </Typography>
              <List dense>
                {alerts.expiry?.slice(0, 2).map((batch, index) => (
                  <ListItem 
                    key={batch._id} 
                    divider={index < 1}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Notifications color="error" sx={{ fontSize: 26 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="h6" fontWeight="medium">
                          {batch.item?.name || 'Unknown Item'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1" color="text.secondary">
                          Expires: {new Date(batch.expiryDate).toLocaleDateString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                {(!alerts.expiry || alerts.expiry.length === 0) && (
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="h6" color="text.secondary">
                          No expiry alerts
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">
                          No items expiring soon
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
    </Container>
    </Box>
  );
};